import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PenSquare, Search, Rss, Eye, ChevronDown, ChevronUp, ExternalLink, Sparkles } from 'lucide-react';
import { useT } from '../../i18n';
import { useOs2, PageHead, Card, LoadingState } from './os2ui';

/* Editorial & Distribution Control Center — AI newsroom, not a CMS.
   Every number is real (Kitxens DB / web events); missing metrics say so. */

const BRAND_SVG = { x: '/brands/x.svg', facebook: '/brands/facebook.svg', linkedin: '/brands/linkedin.svg', instagram: '/brands/instagram.svg', google: '/brands/google.svg', newsletter: '/brands/newsletter.svg' };
const PLATFORM_LABEL = { x: 'X', facebook: 'Facebook', linkedin: 'LinkedIn', instagram: 'Instagram', google: 'Google Business', rss: 'RSS · dlvr.it', newsletter: 'Newsletter' };
const AGENTS = ['penny', 'sonny', 'rachel', 'stan'];

function BrandIcon({ k, className = 'w-4 h-4' }) {
  if (k === 'rss' || k === 'social_package') return <Rss className={`${className} text-amber-500`} />;
  const src = BRAND_SVG[k];
  return src ? <img src={src} alt={PLATFORM_LABEL[k] || k} className={`${className} object-contain`} /> : <Rss className={`${className} text-gray-400`} />;
}

function AgentChip({ a, isEs }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 pl-0.5 pr-2 py-0.5 text-[10px] font-bold text-gray-600 capitalize" data-testid={`content-agent-chip-${a}`}>
      <img src={`/avatars/${a}.png`} alt={a} className="w-4 h-4 rounded-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} /> {a}
    </span>
  );
}

const ago = (iso, isEs) => {
  if (!iso) return '';
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 3600) return `${Math.max(1, Math.round(s / 60))}m`;
  if (s < 86400) return `${Math.round(s / 3600)}h`;
  return `${Math.round(s / 86400)}${isEs ? 'd' : 'd'}`;
};

function LifecyclePreview({ it, isEs }) {
  const lc = it.lifecycle || {};
  const stages = [
    { k: 'draft', es: 'Idea → Borrador', en: 'Idea → Draft', ok: true, sub: it.updated_at ? it.updated_at.slice(0, 10) : '' },
    { k: 'approved', es: 'Aprobado', en: 'Approved', ok: it.status === 'published', sub: '' },
    { k: 'published', es: 'Publicado', en: 'Published', ok: !!lc.published_at, sub: lc.published_at ? lc.published_at.slice(0, 10) : '' },
    { k: 'distributed', es: 'Distribuido', en: 'Distributed', ok: lc.distributed > 0, sub: lc.distributed > 0 ? `${lc.distributed}×` : '' },
    { k: 'traffic', es: 'Tráfico', en: 'Traffic', ok: lc.traffic > 0, sub: `${lc.traffic || 0} views` },
    { k: 'conversions', es: 'Conversiones', en: 'Conversions', ok: false, sub: isEs ? 'sin atribución aún' : 'not attributed yet' },
  ];
  return (
    <div className="mt-3 border-t border-gray-100 pt-3" data-testid={`content-lifecycle-${it.slug}`}>
      <div className="flex flex-wrap items-center gap-1.5">
        {stages.map((s, i) => (
          <React.Fragment key={s.k}>
            {i > 0 && <span className="text-gray-300 text-[10px]">→</span>}
            <span className={`inline-flex flex-col items-start rounded-lg px-2 py-1 border text-[10px] font-bold ${s.ok ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-gray-200 bg-gray-50 text-gray-400'}`}>
              {isEs ? s.es : s.en}
              {s.sub && <span className="font-semibold text-[9px] opacity-80">{s.sub}</span>}
            </span>
          </React.Fragment>
        ))}
      </div>
      {it.previews && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid={`content-previews-${it.slug}`}>
          {Object.entries(it.previews).map(([k, txt]) => (
            <div key={k} className="rounded-xl border border-gray-200 bg-white p-2.5">
              <p className="flex items-center gap-1.5 text-[10.5px] font-bold text-gray-700"><BrandIcon k={k} className="w-3.5 h-3.5" /> {PLATFORM_LABEL[k] || k}</p>
              <p className="mt-1 text-[11.5px] text-gray-500 leading-snug line-clamp-3">{txt}</p>
            </div>
          ))}
        </div>
      )}
      {it.distributions?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {it.distributions.map((d, i) => (
            <span key={i} className="inline-flex items-center gap-1 rounded-full bg-gray-50 border border-gray-200 px-2 py-0.5 text-[10px] text-gray-500">
              <BrandIcon k={d.platform} className="w-3 h-3" /> {d.platform} · {d.status}{d.at ? ` · ${d.at.slice(0, 10)}` : ''}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function ArticleCard({ it, isEs }) {
  const [open, setOpen] = useState(false);
  return (
    <Card className="overflow-hidden hover:shadow-md transition-shadow" data-testid={`content-card-${it.slug}`}>
      <div className="flex items-stretch">
        {it.cover && <img src={it.cover} alt={it.title} loading="lazy" className="w-[110px] sm:w-[140px] object-cover shrink-0" />}
        <div className="p-3.5 min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-bold uppercase tracking-wide ${it.status === 'published' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>{it.status}</span>
            {it.category && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-50 border border-gray-200 text-gray-500">{it.category}</span>}
            <AgentChip a={it.agent} isEs={isEs} />
          </div>
          <a href={`/magazine/${it.slug}`} target="_blank" rel="noopener noreferrer" data-testid={`content-title-${it.slug}`}
            className="mt-1.5 block text-[14.5px] font-semibold text-gray-900 leading-snug hover:text-[#F77F11] transition-colors truncate">
            {it.title} <ExternalLink className="inline w-3 h-3 text-gray-300" />
          </a>
          <p className="mt-0.5 text-[11.5px] text-gray-400 line-clamp-1">{it.excerpt}</p>
          <div className="mt-2 flex items-center gap-3 text-[11px] text-gray-500">
            <span className="inline-flex items-center gap-1 font-bold text-gray-800 tabular-nums"><Eye className="w-3.5 h-3.5 text-gray-400" /> {it.views}</span>
            {it.published_at && <span>{it.published_at.slice(0, 10)}</span>}
            {it.reading_time && <span>{it.reading_time} min</span>}
            <span className="inline-flex items-center gap-1">
              {(it.previews ? Object.keys(it.previews) : []).slice(0, 5).map((k) => <BrandIcon key={k} k={k} className="w-3.5 h-3.5" />)}
            </span>
            <button onClick={() => setOpen((o) => !o)} data-testid={`content-expand-${it.slug}`}
              className="ml-auto inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-wide text-[#F77F11] hover:text-gray-900 transition-colors">
              {isEs ? 'Ciclo de vida' : 'Lifecycle'} {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
          </div>
          {open && <LifecyclePreview it={it} isEs={isEs} />}
        </div>
      </div>
    </Card>
  );
}

function Pagination({ page, pages, go }) {
  const nums = useMemo(() => {
    const out = [];
    for (let i = Math.max(1, page - 2); i <= Math.min(pages, page + 2); i += 1) out.push(i);
    return out;
  }, [page, pages]);
  if (pages <= 1) return null;
  const btn = 'min-w-[32px] h-8 px-2 rounded-lg border text-[12px] font-bold transition-colors disabled:opacity-30';
  return (
    <div className="mt-4 flex items-center justify-center gap-1.5" data-testid="content-pagination">
      <button className={`${btn} border-gray-200 text-gray-500 hover:border-gray-400`} disabled={page === 1} onClick={() => go(1)} data-testid="content-page-first">«</button>
      <button className={`${btn} border-gray-200 text-gray-500 hover:border-gray-400`} disabled={page === 1} onClick={() => go(page - 1)} data-testid="content-page-prev">‹</button>
      {nums.map((n) => (
        <button key={n} onClick={() => go(n)} data-testid={`content-page-${n}`}
          className={`${btn} ${n === page ? 'border-[#F77F11] bg-[#F77F11] text-white' : 'border-gray-200 text-gray-600 hover:border-gray-400'}`}>{n}</button>
      ))}
      <button className={`${btn} border-gray-200 text-gray-500 hover:border-gray-400`} disabled={page === pages} onClick={() => go(page + 1)} data-testid="content-page-next">›</button>
      <button className={`${btn} border-gray-200 text-gray-500 hover:border-gray-400`} disabled={page === pages} onClick={() => go(pages)} data-testid="content-page-last">»</button>
    </div>
  );
}

const FEED_LABEL = {
  NEWSLETTER_SENT: { es: 'Newsletter enviada', en: 'Newsletter sent' },
  ARTICLE_PUBLISHED: { es: 'Artículo publicado', en: 'Article published' },
  SOCIAL_POST_DIRECT: { es: 'Post publicado', en: 'Post published' },
  POST_PUBLISHED: { es: 'Post publicado', en: 'Post published' },
  SOCIAL_PUBLICATION_CONFIRMED: { es: 'Publicación confirmada', en: 'Publication confirmed' },
};

export default function ContentOsPage() {
  const { lang } = useT();
  const isEs = lang === 'es';
  const [page, setPage] = useState(1);
  const [rawQ, setRawQ] = useState('');
  const [q, setQ] = useState('');
  const [platform, setPlatform] = useState('');
  const [agent, setAgent] = useState('');
  const [status, setStatus] = useState('');
  useEffect(() => { const t = setTimeout(() => { setQ(rawQ); setPage(1); }, 300); return () => clearTimeout(t); }, [rawQ]);
  const path = `/content/center?page=${page}${q ? `&q=${encodeURIComponent(q)}` : ''}${platform ? `&platform=${platform}` : ''}${agent ? `&agent=${agent}` : ''}${status ? `&status=${status}` : ''}`;
  const { data: d, err, reload, fetchedAt, loading } = useOs2(path);
  if (err) return <p className="text-sm text-red-600">{err}</p>;
  if (!d) return <LoadingState />;

  const perf = d.performance || {};
  const ins = d.insight || {};
  const pill = (active) => `rounded-full border px-2.5 py-1 text-[11px] font-bold transition-colors ${active ? 'border-[#F77F11] bg-[#FFF3E8] text-[#F77F11]' : 'border-gray-200 text-gray-500 hover:border-gray-400'}`;

  return (
    <div data-testid="os2-content">
      <PageHead
        title={isEs ? 'Editorial & Distribution Control Center' : 'Editorial & Distribution Control Center'}
        question={isEs ? '¿Qué contenido funciona, dónde se distribuye y qué preparo después?' : 'What content works, where does it go, and what should we prepare next?'}
        fresh={{ at: fetchedAt, onReload: reload, loading, isEs }}
        module="AI OS"
        right={
          <Link to="/admin/editor" className="inline-flex items-center gap-1.5 rounded-full bg-gray-900 hover:bg-[#F77F11] text-white text-[12.5px] font-semibold px-4 py-2 transition-colors" data-testid="content-open-editorial">
            <PenSquare className="w-3.5 h-3.5" /> {isEs ? 'Panel Editorial (Penny)' : 'Editorial Panel (Penny)'}
          </Link>
        }
      />

      {/* Social distribution dashboard — official logos, real coverage counts */}
      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="content-platform-strip">
        {(d.platforms || []).map((p) => (
          <button key={p.key} onClick={() => { setPlatform(platform === p.key ? '' : p.key); setPage(1); }}
            data-testid={`content-platform-${p.key}`}
            className={`rounded-2xl border bg-white p-3 text-left transition-all hover:-translate-y-0.5 hover:shadow-md ${platform === p.key ? 'border-[#F77F11] ring-1 ring-[#F77F11]' : 'border-gray-200'}`}>
            <BrandIcon k={p.key} className="w-6 h-6" />
            <p className="mt-1.5 text-[10.5px] font-bold text-gray-700 leading-tight">{p.key === 'x' ? '\u00A0' : (PLATFORM_LABEL[p.key] || p.key)}</p>
            <p className="text-[15px] font-black tabular-nums text-gray-900">{p.n}<span className="ml-1 text-[9px] font-bold text-gray-400 uppercase">{p.key === 'newsletter' ? (isEs ? 'envíos' : 'sends') : 'posts'}</span></p>
          </button>
        ))}
      </div>

      {/* Rachel insight — computed from real numbers, never invented */}
      <Card className="mt-4 p-4 flex items-start gap-3 border-l-4 border-l-[#F77F11]" data-testid="content-insight">
        <img src="/avatars/rachel.png" alt="Rachel" className="w-10 h-10 rounded-full object-cover shrink-0" />
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#F77F11] flex items-center gap-1"><Sparkles className="w-3 h-3" /> Rachel · AI Insights</p>
          <p className="mt-1 text-[13px] text-gray-700 leading-relaxed">
            {isEs
              ? <>El artículo más leído es <b>{ins.top_title || '—'}</b> con <b>{ins.top_views || 0}</b> lecturas.{' '}
                {typeof ins.trend_pct === 'number' ? <>El tráfico editorial va <b className={ins.trend_pct >= 0 ? 'text-emerald-600' : 'text-red-500'}>{ins.trend_pct >= 0 ? '+' : ''}{ins.trend_pct}%</b> esta semana vs la anterior.</> : 'Aún no hay dos semanas completas de tráfico para comparar.'}</>
              : <>Your most-read article is <b>{ins.top_title || '—'}</b> with <b>{ins.top_views || 0}</b> reads.{' '}
                {typeof ins.trend_pct === 'number' ? <>Editorial traffic is <b className={ins.trend_pct >= 0 ? 'text-emerald-600' : 'text-red-500'}>{ins.trend_pct >= 0 ? '+' : ''}{ins.trend_pct}%</b> this week vs last.</> : 'Not enough weekly data to compare yet.'}</>}
          </p>
          <p className="mt-1 text-[12px] font-semibold text-gray-500">
            {ins.drafts > 0
              ? (isEs ? `Recomendación: Penny tiene ${ins.drafts} borrador${ins.drafts > 1 ? 'es' : ''} esperando tu aprobación — nada se publica sin ti.` : `Recommendation: Penny has ${ins.drafts} draft${ins.drafts > 1 ? 's' : ''} waiting for your approval — nothing publishes without you.`)
              : (isEs ? 'Recomendación: la cola editorial está limpia — buen momento para encargar el siguiente tema a Penny.' : 'Recommendation: the editorial queue is clear — a good moment to brief Penny on the next topic.')}
          </p>
        </div>
      </Card>

      {/* Filters + instant search */}
      <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="content-filters">
        <span className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input value={rawQ} onChange={(e) => setRawQ(e.target.value)} placeholder={isEs ? 'Buscar contenido…' : 'Search content…'}
            data-testid="content-search"
            className="h-8 w-52 rounded-full border border-gray-200 bg-white pl-8 pr-3 text-[12px] outline-none focus:border-[#F77F11] transition-colors" />
        </span>
        {AGENTS.map((a) => (
          <button key={a} onClick={() => { setAgent(agent === a ? '' : a); setPage(1); }} className={`${pill(agent === a)} capitalize inline-flex items-center gap-1`} data-testid={`content-filter-agent-${a}`}>
            <img src={`/avatars/${a}.png`} alt={a} className="w-3.5 h-3.5 rounded-full object-cover" /> {a}
          </button>
        ))}
        <span className="w-px h-5 bg-gray-200" />
        {['published', 'draft'].map((s) => (
          <button key={s} onClick={() => { setStatus(status === s ? '' : s); setPage(1); }} className={pill(status === s)} data-testid={`content-filter-status-${s}`}>
            {s === 'published' ? (isEs ? 'Publicados' : 'Published') : (isEs ? 'Borradores' : 'Drafts')}
          </button>
        ))}
        {(platform || agent || status || q) && (
          <button onClick={() => { setPlatform(''); setAgent(''); setStatus(''); setRawQ(''); setPage(1); }}
            className="text-[11px] font-bold text-gray-400 hover:text-gray-800 transition-colors" data-testid="content-filter-clear">
            {isEs ? '× Limpiar' : '× Clear'}
          </button>
        )}
        <span className="ml-auto text-[11px] text-gray-400 font-semibold" data-testid="content-total">{d.total} {isEs ? 'piezas' : 'pieces'}</span>
      </div>

      <div className="mt-3 grid grid-cols-1 xl:grid-cols-3 gap-4">
        {/* Gallery — Sonny & Penny's portfolio */}
        <div className="xl:col-span-2" data-testid="content-gallery">
          <div className="space-y-3">
            {d.items.length ? d.items.map((it) => <ArticleCard key={it.slug} it={it} isEs={isEs} />)
              : <Card className="p-8 text-center text-[13px] text-gray-400">{isEs ? 'Nada coincide con esos filtros.' : 'Nothing matches those filters.'}</Card>}
          </div>
          <Pagination page={d.page} pages={d.pages} go={setPage} />
        </div>

        {/* Right rail: activity, timeline, performance */}
        <div className="space-y-4">
          <Card className="p-4" data-testid="content-feed">
            <h2 className="text-[13.5px] font-semibold tracking-tight flex items-center gap-2">
              <img src="/avatars/sonny.png" alt="Sonny" className="w-5 h-5 rounded-full object-cover" /> {isEs ? 'Actividad del equipo AI' : 'AI team activity'}
            </h2>
            <div className="mt-2 space-y-2 max-h-64 overflow-y-auto">
              {(d.feed || []).map((f, i) => (
                <div key={i} className="flex items-start gap-2 text-[12px]">
                  <img src={`/avatars/${f.agent}.png`} alt={f.agent} className="w-5 h-5 rounded-full object-cover mt-0.5 shrink-0"
                    onError={(e) => { e.target.src = '/avatars/penny.png'; }} />
                  <p className="text-gray-600 leading-snug min-w-0">
                    <b className="capitalize">{f.agent}</b> · {(FEED_LABEL[f.type] || {})[isEs ? 'es' : 'en'] || f.type}
                    {f.network && <> · <span className="uppercase text-[10px] font-bold text-gray-400">{f.network}</span></>}
                    {f.title && <span className="block text-gray-400 truncate">{f.title}</span>}
                  </p>
                  <span className="ml-auto shrink-0 text-[10px] text-gray-300 font-bold">{ago(f.at, isEs)}</span>
                </div>
              ))}
              {!(d.feed || []).length && <p className="text-[12px] text-gray-400">{isEs ? 'Sin actividad registrada aún.' : 'No recorded activity yet.'}</p>}
            </div>
          </Card>

          <Card className="p-4" data-testid="content-timeline">
            <h2 className="text-[13.5px] font-semibold tracking-tight">{isEs ? 'Timeline de distribución' : 'Distribution timeline'}</h2>
            {[['today', isEs ? 'Hoy' : 'Today'], ['yesterday', isEs ? 'Ayer' : 'Yesterday'],
              ['this_week', isEs ? 'Esta semana' : 'This week'], ['last_week', isEs ? 'Semana pasada' : 'Last week']].map(([k, lbl]) => (
                <div key={k} className="mt-2" data-testid={`content-timeline-${k}`}>
                  <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400">{lbl} <span className="text-gray-300">· {(d.timeline?.[k] || []).length}</span></p>
                  {(d.timeline?.[k] || []).slice(0, 4).map((t, i) => (
                    <p key={i} className="mt-1 flex items-center gap-1.5 text-[11.5px] text-gray-600 truncate">
                      <BrandIcon k={t.platform} className="w-3 h-3 shrink-0" /> <span className="truncate">{t.title || t.platform}</span>
                      <span className="ml-auto shrink-0 text-[10px] text-gray-300">{t.status}</span>
                    </p>
                  ))}
                  {!(d.timeline?.[k] || []).length && <p className="mt-0.5 text-[11px] text-gray-300">{isEs ? 'Sin distribuciones' : 'No distributions'}</p>}
                </div>
              ))}
          </Card>

          <Card className="p-4" data-testid="content-performance">
            <h2 className="text-[13.5px] font-semibold tracking-tight">{isEs ? 'Performance Snapshot' : 'Performance Snapshot'}</h2>
            <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2.5">
              {[[isEs ? 'Lecturas totales' : 'Total reads', perf.views_total],
                [isEs ? 'Lecturas esta semana' : 'Reads this week',
                  <>{perf.views_this_week}{typeof perf.views_trend_pct === 'number' && <span className={`ml-1 text-[10px] font-bold ${perf.views_trend_pct >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{perf.views_trend_pct >= 0 ? '↗ +' : '↘ '}{perf.views_trend_pct}%</span>}</>],
                [isEs ? 'Conversiones 14d' : 'Conversions 14d', perf.conversions_14d],
                [isEs ? 'Newsletters enviadas' : 'Newsletters sent', perf.newsletters_sent],
                [isEs ? 'Publicados' : 'Published', perf.published],
                [isEs ? 'Packs sociales' : 'Social packs', perf.packs]].map(([k, v]) => (
                  <div key={k}>
                    <p className="text-[9.5px] uppercase tracking-wider font-bold text-gray-400">{k}</p>
                    <p className="text-[16px] font-black tabular-nums text-gray-900">{v ?? 0}</p>
                  </div>
                ))}
            </div>
            <p className="mt-2 text-[10px] text-gray-300">{isEs ? 'Fuente: Kitxens DB + web events reales. Métricas no rastreadas se declaran, nunca se inventan.' : 'Source: Kitxens DB + real web events. Untracked metrics are declared, never invented.'}</p>
          </Card>
        </div>
      </div>
    </div>
  );
}
